<template>
  <div class="mt-2">
    <WarningDisable permission="bruteforce" />
    <div class="flex gap-6">
      <div>
        <form
          class="mb-4 space-y-4"
          @submit="submit_without_reloading"
          @reset="keyword = undefined"
        >
          <div class="space-y-2">
            <div>
              <legend>Từ khóa</legend>
              <label class="input w-full md:w-fit 2xl:w-fit">
                <i
                  class="fa-solid fa-magnifying-glass text-base-content/80"
                ></i>
                <input
                  type="text"
                  placeholder="Tìm kiếm theo từ khóa"
                  name="keyword"
                  v-model="keyword"
                />
              </label>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <input
              class="btn btn-outline grow"
              type="reset"
              value="&#8635; Đặt lại"
              :disabled="loading"
            />
            <button
              class="btn btn-primary btn-outline grow"
              type="submit"
              :disabled="!filter_has_changed || loading"
            >
              &#10003; Áp dụng
            </button>
          </div>
        </form>
      </div>

      <div class="flex grow flex-col overflow-x-auto">
        <!-- Results -->
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <h2 class="card-title">
                <i class="fa-solid fa-list mr-2"></i>
                Kết quả dò mật khẩu
              </h2>
              <div class="flex gap-2">
                <button
                  class="btn btn-sm btn-outline"
                  @click="refreshResults"
                  :disabled="loading"
                >
                  <i class="fa-solid fa-refresh mr-1"></i>
                  Làm mới
                </button>
              </div>
            </div>

            <!-- Statistics -->
            <div
              v-if="results.length > 0"
              class="stats stats-horizontal mb-4 shadow"
            >
              <div class="stat">
                <div class="stat-title">Tổng kết quả</div>
                <div class="stat-value">{{ results.length }}</div>
              </div>
              <div class="stat">
                <div class="stat-title">IP thành công</div>
                <div class="stat-value text-success">
                  {{ uniqueIPs.length }}
                </div>
              </div>
              <div class="stat">
                <div class="stat-title">User thành công</div>
                <div class="stat-value text-info">{{ uniqueUsers.length }}</div>
              </div>
            </div>

            <!-- Loading -->
            <Loading v-if="loading" />

            <!-- Results Table -->
            <div v-else-if="results.length > 0" class="overflow-x-auto">
              <table class="table-pin-rows table">
                <thead>
                  <tr>
                    <th></th>
                    <th>IP</th>
                    <th>Username</th>
                    <th>Password</th>
                    <th>Số lần brute-force</th>
                    <th>Thời gian phát hiện</th>
                    <th>Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(result, idx) in results"
                    :key="result.id"
                    class="hover"
                  >
                    <th>{{ Number(idx) + 1 }}</th>
                    <td>
                      <code class="badge badge-outline">{{ result.ip }}</code>
                    </td>
                    <td>
                      <div
                        v-if="isFilePath(result.username)"
                        class="flex items-center gap-2"
                      >
                        <span
                          class="text-sm italic opacity-75"
                          :title="result.username"
                          >{{ getFileName(result.username) }}</span
                        >
                        <button
                          class="btn btn-xs btn-outline"
                          @click="
                            downloadUsernameFile(result.id, result.username)
                          "
                          title="Tải xuống"
                        >
                          <i class="fa-solid fa-download"></i>
                        </button>
                      </div>
                      <div v-else class="flex items-center gap-2">
                        <span>{{ result.username }}</span>
                        <button
                          v-if="result.username"
                          class="btn btn-xs btn-ghost"
                          @click="copyToClipboard(result.username)"
                        >
                          <i class="fa-solid fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td>
                      <div
                        v-if="isFilePath(result.password)"
                        class="flex items-center gap-2"
                      >
                        <span
                          class="text-sm italic opacity-75"
                          :title="result.password"
                          >{{ getFileName(result.password) }}</span
                        >
                        <button
                          class="btn btn-xs btn-outline"
                          @click="
                            downloadPasswordFile(result.id, result.password)
                          "
                          title="Tải xuống"
                        >
                          <i class="fa-solid fa-download"></i>
                        </button>
                      </div>
                      <div v-else class="flex items-center gap-2">
                        <span>{{ result.password }}</span>
                        <button
                          v-if="result.password"
                          class="btn btn-xs btn-ghost"
                          @click="copyToClipboard(result.password)"
                        >
                          <i class="fa-solid fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        class="btn btn-xs btn-outline"
                        @click="openBruteForceDetails(result)"
                      >
                        {{ result.bruteforce ? result.bruteforce.length : 0 }}
                      </button>
                    </td>
                    <td>{{ dayjs(result.created).fromNow() }}</td>
                    <td>
                      <div class="flex gap-1">
                        <button
                          v-if="result.username && result.password"
                          class="btn btn-xs btn-outline btn-error"
                          @click="openExploit(result)"
                          title="Khai thác"
                        >
                          Khai thác
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- No Results -->
            <div v-else class="alert alert-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>Chưa có kết quả nào được tìm thấy.</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BruteForceExploitModal
      v-model:open="showExploitModal"
      :target="selectedExploitTarget"
      @exploited="handleExploitSuccess"
    />

    <BruteForceResultModal
      v-model:open="showBruteForceDetailsModal"
      :result="selectedBruteForceDetails"
    />
  </div>
</template>

<script setup lang="ts">
import { str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import Loading from "@/components/LoadingTemplate.vue";
import { downloadFileFromUrl } from "@/composables/useDownload";
import dayjs from "dayjs";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import WarningDisable from "../WarningDisable.vue";
import BruteForceExploitModal from "./BruteForceExploitModal.vue";
import BruteForceResultModal from "./BruteForceResultModal.vue";

interface BruteForceResult {
  id: string;
  agent_id: string;
  scan_id: string;
  ip: string;
  username: string;
  password: string;
  created: number;
  modified: number;
  bruteforce?: {
    id: string;
    time: Date;
    status: string;
    time_brute_force: Date;
    completed: Date;
    result: {
      ip: string;
      ps: string;
      ret: boolean;
      type: string;
      us: string;
    }[];
  }[];
}

const route = useRoute();
const router = useRouter();
const action_id = route.params.action_id as string;

const loading = ref(false);
const results = ref<BruteForceResult[]>([]);

// Filter state
const init_keyword = computed(() => str_from_query("keyword"));
const keyword = ref<string | undefined>(init_keyword.value);
const filter_has_changed = computed(() => keyword.value !== init_keyword.value);

// Exploit Modal State
const showExploitModal = ref(false);
const selectedExploitTarget = ref<BruteForceResult | null>(null);

// Brute Force Details Modal State
const showBruteForceDetailsModal = ref(false);
const selectedBruteForceDetails = ref<BruteForceResult | null>(null);

// Computed properties
const uniqueIPs = computed(() => [...new Set(results.value.map((r) => r.ip))]);
const uniqueUsers = computed(() => [
  ...new Set(results.value.map((r) => r.username)),
]);

onMounted(() => {
  loadResults();
});

const loadResults = async () => {
  try {
    loading.value = true;

    const response = await axios_instance.post(
      "/api/v1/view/password-detection/pagination",
      {
        keyword: keyword.value || "",
        action_id: action_id,
        state: [],
        protocol: [],
        page: 1,
        size: 100,
      },
    );

    results.value = response.data.data || [];
  } catch (error) {
    console.error("Error loading results:", error);
    toast.error("Không thể tải kết quả dò mật khẩu!");
  } finally {
    loading.value = false;
  }
};

const refreshResults = () => {
  loadResults();
  toast.success("Đã làm mới kết quả!");
};

const submit_without_reloading = (e: Event) => {
  e.preventDefault();
  router.push({
    name: router.currentRoute.value.name,
    query: {
      ...router.currentRoute.value.query,
      page: 1,
      keyword: keyword.value,
    },
  });
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Đã sao chép vào clipboard!");
  } catch (error) {
    console.error("Error copying to clipboard:", error);
    toast.error("Không thể sao chép!");
  }
};

const isFilePath = (text: string) => {
  return text.startsWith("./tmp/");
};

const getFileName = (path: string) => {
  return path.split("/").pop() || path;
};

const downloadPasswordFile = (id: string, path: string) => {
  const url = `/api/v1/view/password-detection/password/${id}`;
  const filename = getFileName(path);
  downloadFileFromUrl(url, filename);
};

const downloadUsernameFile = (id: string, path: string) => {
  const url = `/api/v1/view/password-detection/user/${id}`;
  const filename = getFileName(path);
  downloadFileFromUrl(url, filename);
};

const openExploit = (result: BruteForceResult) => {
  selectedExploitTarget.value = result;
  showExploitModal.value = true;
};

const openBruteForceDetails = (result: BruteForceResult) => {
  selectedBruteForceDetails.value = result;
  showBruteForceDetailsModal.value = true;
};

const handleExploitSuccess = () => {
  // Maybe refresh results or log something
  console.log("Exploit command sent successfully");
};
</script>
